// Cluster ZUWEISEN — „Kontakt zuweisen" als Light-Dialog (WindowManager-Fenster,
// zentriert, mobil Vollbild).
//
// B1: ZWEI Wege untereinander statt fünf gleichrangiger Pillen.
//   Weg 1 „Bestehenden Kontakt suchen" (oben, autofokussiert)
//   Weg 2 „Neuen Kontakt erstellen"    (darunter, zugeklappt)
// Der Firma-Zweig legt immer eine NEUE Firma an (optional mit Ansprechpartner).
// Ein neuer Ansprechpartner bei einer BESTEHENDEN Firma entsteht über den
// Personen-Zweig: Feld „Ansprechpartner bei Firma" (früher der Umschalter
// „neu anlegen / bestehende wählen" im Firma-Zweig — PO 01.10.2026: „ich gebe
// doch schon an, dass ich neuen Kontakt anlegen möchte").
//
// T-27 (PO 01.10.2026): Die Fußzeile hat nur noch „Nur Zuweisen | Weiter"
// (Treffer), „Nur Anlegen | Weiter" (Neuanlage) oder „Weiter" mit der
// Rückfrage „Ohne Kontakt fortfahren?". „Abbrechen" und der Textlink „Ohne
// Kontakt fortfahren" sind entfallen — geschlossen wird über das X.
//
// B5: Die Zuweisung ist ein abschließbarer Vorgang. Das Fenster ENTSCHEIDET
// nichts über MailLink oder Archivieren — es liefert ein `MailZuweisungErgebnis`
// mit `weiter: 'zuweisen' | 'vorgang'`; den Abschluss fährt der Aufrufer über
// `schliesseZuweisungAb` (erst verknüpfen, dann archivieren). Früher lag das
// Verknüpfen hier — best-effort und nur im Bestandskontakt-Weg; für neu
// angelegte Kontakte passierte es gar nicht.
//
// B2: keine Fluent-FORMULARkomponente. `makeStyles` (Styling-Engine) und die
// Icon-Bibliothek sind Hausstandard der `forms/`-Bausteine und bleiben.
//
// Reine Ableitungslogik/Payloads: mailContactAssign.ts, mailKontaktAnlage.ts.

import * as React from "react";
import { PersonAdd20Regular } from "@fluentui/react-icons";
import { useWindowManager, type OpenWindowConfig } from "../windowManager/WindowManagerContext";
import {
    DuplicateWarningPanel,
    EmailInput,
    KontaktSuchfeld,
    LabeledInput,
    LabeledSelect,
    LegalFormSelect,
    PhoneInput,
    StatusSegmented,
    useDuplicateCheck,
    type KontaktTreffer,
} from "../forms";
import OwnershipSection from "../forms/OwnershipSection";
import { KontaktStatusSchritt, type KontaktTyp } from "../forms/KontaktStatusSchritt";
import { ConfirmDialog } from "../redesign/ConfirmDialog";
import { useAuth } from "../../context/AuthContext";
import { rollenAusFeld, statusFuerApi } from "../../data/kontaktstatusSegment";
import { typAusRollen } from "../../data/kontaktRollen";
import { attachMenschen } from "../../services/menschenFuersUnternehmenService";
import { apiUrl } from "../../utils/api";
import type { FormState } from "../../utils/formUtils";
import {
    adresseArtVorbelegung,
    apPflichtOk,
    buildAttachEmailPayload,
    extractCreatedContactId,
    firmaPflichtOk,
    fusszeileFuer,
    hasDefaultEmail,
    hasSameEmail,
    interneDomaenenAusPostfaechern,
    istInterneAdresse,
    prefillEmail,
    privatPflichtOk,
    startTypNeu,
    vorbelegungAusMail,
    type AssignOwnership,
    type FirmaWerte,
    type KommArt,
    type KontaktRef,
    type MailZuweisungErgebnis,
    type NeuTyp,
    type PrivatWerte,
    type SignaturVorbelegung,
} from "./mailContactAssign";
import {
    legeKontaktAn,
    mailVollanlageVorbereiten,
    standNachVollanlage,
    type AnlageEingabe,
    type AnlageStand,
} from "./mailKontaktAnlage";
import { VollanlageWeiterKnopf } from "../forms/VollanlageWeiter";
import { contactCommunicationService, type ContactCommMeta } from "../../services/contactCommunicationService";
import { vorgangStartetMitFirma } from "../vorgang/create/vorgangFuer";
import { kisGruppe } from "../forms/widgets/kontaktTrefferAnzeige";
import { sucheFirmaNachNamen } from "../../services/kontaktSucheService";
import { fetchLegalFormOptions, rechtsformAusName, type LegalFormOption } from "../../utils/legalFormOptions";
import type { CreateMailLinkInput } from "../../services/mailLinkService";
import { useMailContext } from "../../context/MailContext";
import { reportMailError } from "../../services/mailError";
import { arbeitgeberSetzen } from "../../services/arbeitgeberService";
import {
    VerantwortlichkeitInput,
    type VerantwortlichkeitValue,
} from "../verantwortlichkeit/VerantwortlichkeitInput";
import { useVerantwortlichkeiten } from "../verantwortlichkeit/useVerantwortlichkeiten";
import {
    LEERE_VERANTWORTLICHKEIT,
    hatVerbindungsAngabe,
    istMenschImUnternehmen,
    verantAusSignatur,
    verbindungsHinweis,
} from "./mailVerantwortlichkeit";

const AUB = "#4d2051";
const BALKEN = "#7d2169"; // vgd-aub (lila Balken, wie im Detail-Remake)

/** Erkennungs-Anteile, die das Popup verwertet (alles optional — Cluster ERKENNUNG). */
export type MailZuweisungErkennung = {
    signature?: SignaturVorbelegung;
    companyDomain?: {
        domain: string;
        companies: Array<{ contactId: number; displayName: string }>;
        ambiguous: boolean;
    } | null;
    /** Der Absender ist ein eigener Kollege (B6/A2). */
    intern?: boolean;
} | null;

export interface OpenMailContactAssignParams {
    senderEmail: string | null;
    senderName?: string | null;
    /**
     * B3/B5 — der Schnappschuss der Mail. Der Aufrufer braucht ihn, um die MAIL
     * mit dem Kontakt zu verknüpfen; das Fenster reicht ihn nur durch und fasst
     * ihn selbst nicht mehr an.
     */
    mail?: CreateMailLinkInput;
    /** Aktuell zugewiesener/erkannter Kontakt — zeigt „Aktuell zugewiesen: …" (B4). */
    aktuelleZuweisung?: { id: number; displayName: string } | null;
    /** Erkennung aus GET /mail/recognize (Cluster ERKENNUNG). */
    erkennung?: MailZuweisungErkennung;
    /**
     * Vorauswahl des Ausgangs: aus „Neuer Vorgang" heraus 'vorgang', sonst 'zuweisen'.
     * Seit T-27 ohne Wirkung — die Fußzeile zeigt immer beide Ausgänge
     * („Nur Zuweisen | Weiter"). Bleibt, damit die Aufrufer unverändert bleiben.
     */
    standardAbschluss?: "zuweisen" | "vorgang";
    /**
     * `nur-anlegen` blendet jeden Mail-Bezug aus: kein „Zuweisen", kein
     * Vorgangs-Ausgang, kein Hinweis aufs Archivieren.
     *
     * Gebraucht für die Visitenkarte im Anhang (`vcard.ts`): Die Karte gehört
     * fast nie dem Absender — jemand LEITET sie weiter. Würde dieser Weg die
     * Mail zuweisen, landete sie beim Falschen und wäre archiviert dazu.
     */
    modus?: "zuweisen" | "nur-anlegen" | "zusaetzlich";
    /**
     * T-36: Der in der Mail markierte Text, aus dem angelegt wird. Erkennt der
     * Parser nichts, steht er als Hinweis über der Maske — geraten wird nichts.
     */
    markierterText?: string | null;
    /**
     * Zugewiesen wird keine Mail, sondern etwas anderes (z. B. ein Eintrag der
     * DVAG-Kontakthistorie): eigene Kopfzeile, und die Hinweise sprechen vom
     * Eintrag statt von der Mail. Archiviert wird dort nichts.
     */
    gegenstand?: { kopf: React.ReactNode; name: string };
    /**
     * Eigener Satz unten in der Fußzeile — wo „Nur …“ und „Weiter“ etwas
     * anderes bedeuten als im Posteingang (Zuweisen beim Antworten: nichts
     * wird archiviert, „Weiter“ wählt einen Vorgang statt einen anzulegen).
     */
    fussHinweis?: string;
    onResolved: (ergebnis: MailZuweisungErgebnis) => void;
}

type TypeIds = { firma: number | null; person: number | null };

/** Auswahlwert „andere Firma suchen" in der Firmenwahl der geschäftlichen Adresse (T-27). */
const ANDERE_FIRMA = "__andere";

/** Verbundene Firmen eines Treffers — `firmen` (T-26), sonst die eine bekannte Firma. */
function firmenVon(t: KontaktTreffer): Array<{ id: number; name: string }> {
    if (t.firmen?.length) return t.firmen.map((f) => ({ id: f.id, name: f.name }));
    return t.companyId != null ? [{ id: t.companyId, name: t.companyName ?? "Firma" }] : [];
}

/** Contact-Typ-ids einmal auflösen (Muster persistRelations.resolveTypeIds). */
async function resolveTypeIds(): Promise<TypeIds> {
    try {
        const res = await fetch(apiUrl("contacttype"));
        const list = await res.json();
        const find = (n: string) =>
            Array.isArray(list)
                ? (list.find((t: any) => String(t?.type).toLowerCase() === n)?.id ?? null)
                : null;
        return { firma: find("firma"), person: find("person") };
    } catch {
        return { firma: null, person: null };
    }
}

async function postContact(body: Record<string, unknown>): Promise<number | null> {
    const res = await fetch(apiUrl("contact"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });
    if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt ? `${res.status} — ${txt}` : `Anlage fehlgeschlagen (${res.status})`);
    }
    return extractCreatedContactId(await res.json().catch(() => null));
}

async function postRelation(body: Record<string, unknown>): Promise<unknown> {
    const res = await fetch(apiUrl("contact-relation"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
    });
    if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(txt ? `${res.status} — ${txt}` : `Verbindung fehlgeschlagen (${res.status})`);
    }
    return res.json().catch(() => null);
}

const MailContactAssignWindow: React.FC<OpenMailContactAssignParams & { windowId: string }> = ({
    windowId,
    senderEmail,
    senderName,
    aktuelleZuweisung,
    erkennung,
    modus = "zuweisen",
    markierterText,
    gegenstand,
    fussHinweis,
    onResolved,
}) => {
    const { closeWindow } = useWindowManager();
    const { hasPermission } = useAuth();
    // B6: die eigenen Domänen kommen aus den Postfächern des Mandanten —
    // dieselbe Quelle wie im Backend, ohne zusätzliche Konfiguration.
    const { mailboxes } = useMailContext();
    const interneDomaenen = React.useMemo(
        () => interneDomaenenAusPostfaechern(mailboxes.map((m) => m.mailboxAddress)),
        [mailboxes],
    );

    // C4/A5: Signatur schlägt Absendername; ohne Erkennung bleibt der Absender.
    const vorbelegung = React.useMemo(
        () =>
            vorbelegungAusMail({
                senderEmail: senderEmail ?? null,
                senderName: senderName ?? null,
                signature: erkennung?.signature ?? null,
                interneDomaenen,
                // T-43: Bei der Visitenkarte gehört der Name der Karte, nicht
                // dem Absender — dort sperrt nur die Domäne die Adresse (B6).
                intern: modus === "nur-anlegen" ? false : erkennung?.intern ? true : undefined,
            }),
        [senderEmail, senderName, erkennung, interneDomaenen, modus],
    );

    const domainFirmen = erkennung?.companyDomain?.companies ?? [];

    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [typeIds, setTypeIds] = React.useState<TypeIds>({ firma: null, person: null });
    // Teilerfolge einer mehrschrittigen Anlage — der Wiederholversuch überspringt sie.
    const [stand, setStand] = React.useState<AnlageStand>({});

    React.useEffect(() => {
        void resolveTypeIds().then(setTypeIds);
    }, []);

    // ── Weg 1 ────────────────────────────────────────────────────────────────
    const [treffer, setTreffer] = React.useState<KontaktTreffer | null>(null);
    const [selMeta, setSelMeta] = React.useState<ContactCommMeta | null>(null);
    const [selMetaLoading, setSelMetaLoading] = React.useState(false);
    const [adresseHinterlegen, setAdresseHinterlegen] = React.useState(true);
    const [adresseArt, setAdresseArt] = React.useState<KommArt>("privat");
    // C8/T-27 — bei „geschäftlich" wird die Adresse einer Firma zugeordnet:
    // einer der verbundenen Firmen (Auswahl, Wert = Firmen-id) oder einer
    // gesuchten (Wert ANDERE_FIRMA + Suchfeld).
    const [adresseFirmaWahl, setAdresseFirmaWahl] = React.useState<string>("");
    const [adresseFirmaSuche, setAdresseFirmaSuche] = React.useState<KontaktTreffer | null>(null);
    // T-8 + T-27 in EINER Frage: „privat | geschäftlich" (adresseArt) samt
    // Firma steuert die Art der Absenderadresse UND, auf wen ein neuer Vorgang
    // läuft — privat auf die Person, geschäftlich auf die gewählte Firma.

    // ── Weg 2 ────────────────────────────────────────────────────────────────
    // PO 28.09.2026 („Kontakt aus Mail einfacher"): Ist der Absender unbekannt,
    // ist Anlegen der wahrscheinliche Weg — die Maske steht dann schon offen
    // und vorbelegt, statt hinter „+ Neuen Kontakt erstellen" zu warten. Die
    // Suche oben bleibt fokussiert; ein Treffer dort klappt die Maske wieder zu.
    const [neuOffen, setNeuOffen] = React.useState(
        () =>
            modus === "nur-anlegen" ||
            (modus === "zuweisen" && !aktuelleZuweisung && !erkennung?.intern),
    );
    const [verworfen, setVerworfen] = React.useState<string | null>(null);
    // T-43: Bei einem Kollegen als Absender sagt die Domäne nichts über den
    // Kunden — die Anlage startet dann auf „Privat" statt auf „Firma".
    const absenderIntern =
        modus !== "nur-anlegen" &&
        (!!erkennung?.intern || istInterneAdresse(senderEmail, interneDomaenen));
    // T-333: Die Firma aus der Signatur — gesucht wird sie, wenn die Domäne
    // keine bekannte Firma ergibt (sonst entscheidet F.0).
    const signaturFirma = (erkennung?.signature?.company?.value ?? "").trim();
    const signaturFirmaSuchen = !absenderIntern && domainFirmen.length === 0 && signaturFirma !== "";
    const [neuTypGewaehlt, setNeuTypRoh] = React.useState<NeuTyp>(() =>
        // T-36: Nennt die Signatur eine Firma, startet die Anlage auf „Firma" —
        // es sei denn, KMVS kennt die Firma der Domäne schon (F.0): dann ist es
        // ein neuer Ansprechpartner, und der entsteht im Personen-Zweig. Kennt
        // KMVS sie unter ihrem NAMEN, wechselt die Suche unten den Zweig (T-333).
        signaturFirmaSuchen
            ? "firma"
            : startTypNeu({ domainFirmen: domainFirmen.length, senderEmail, intern: absenderIntern }),
    );
    // Ein Klick auf Firma | Privat ist eine Entscheidung — die späte Antwort
    // der Namenssuche darf sie nicht umwerfen.
    const typBeruehrt = React.useRef(false);
    const setNeuTyp = React.useCallback((t: NeuTyp) => {
        typBeruehrt.current = true;
        setNeuTypRoh(t);
    }, []);
    // T-27: Status wie in der Vollanlage — Kunde | Interessent | Sonstige, bei
    // Sonstige Bereich/Rolle. Die Felder sind die des Bausteins
    // `KontaktStatusSchritt` (kontaktStatus, kontaktStatusRollen, kontaktStatusAuch).
    const [statusFelder, setStatusFelder] = React.useState<Record<string, string>>({
        kontaktStatus: "Interessent",
    });
    const statusF = React.useCallback((k: string) => statusFelder[k] ?? "", [statusFelder]);
    const statusSet = React.useCallback(
        (k: string, v: string) => setStatusFelder((o) => ({ ...o, [k]: v })),
        [],
    );
    const statusWahl = statusFuerApi({
        kontaktStatus: statusF("kontaktStatus"),
        rollen: rollenAusFeld(statusF("kontaktStatusRollen")),
        auchKundeOderInteressent: statusF("kontaktStatusAuch"),
    });
    // Legt eine gewählte Rolle den Typ fest (z. B. „Produktpartner" → Firma),
    // gilt dieser — wie in der Vollanlage.
    const typAusRolle =
        statusF("kontaktStatus") === "Sonstige"
            ? typAusRollen(rollenAusFeld(statusF("kontaktStatusRollen")))
            : null;
    const neuTyp: NeuTyp =
        typAusRolle === "firma" ? "firma" : typAusRolle === "person" ? "privat" : neuTypGewaehlt;
    const [firmenname, setFirmenname] = React.useState(vorbelegung.firmenname);
    const [gesellschaftsform, setGesellschaftsform] = React.useState("");
    const [legalFormId, setLegalFormId] = React.useState("");
    // Ohne Personennamen (info@…, Funktionspostfach) gehört die Absenderadresse
    // der Firma — dann steht sie gleich dort.
    const [firmaEmail, setFirmaEmail] = React.useState(() =>
        vorbelegung.lastName ? "" : vorbelegung.email,
    );
    // Rechtsform aus dem Firmennamen („… GmbH" → GmbH), solange der Nutzer sie
    // nicht selbst gewählt hat — dieselbe Erkennung wie in der Vollanlage.
    const rechtsformBeruehrt = React.useRef(false);
    const [rechtsformOptionen, setRechtsformOptionen] = React.useState<LegalFormOption[]>([]);
    React.useEffect(() => {
        let lebt = true;
        fetchLegalFormOptions()
            .then((liste) => {
                if (lebt) setRechtsformOptionen(liste);
            })
            .catch(() => {
                /* ohne Katalog bleibt die Auswahl von Hand */
            });
        return () => {
            lebt = false;
        };
    }, []);
    React.useEffect(() => {
        if (rechtsformBeruehrt.current || !rechtsformOptionen.length) return;
        const { befund, option } = rechtsformAusName(firmenname, rechtsformOptionen);
        if (befund.konfidenz === "SICHER" && option && !befund.trunkiert) {
            setGesellschaftsform(option.legalForm);
            setLegalFormId(String(option.id));
        }
    }, [firmenname, rechtsformOptionen]);
    // C4: Telefon/Mobil in JEDEM Zweig, vorbelegt aus der Signatur (A5). Ohne
    // Signatur bleiben die Felder leer — die Erkennung ist optional.
    const [firmaTelefon, setFirmaTelefon] = React.useState(vorbelegung.telefon);
    const [firmaMobil, setFirmaMobil] = React.useState(vorbelegung.mobil);
    // T-36: Anschrift aus der Signatur → Unternehmenssitz der neuen Firma. Die
    // Felder stehen offen, sobald die Signatur eine Anschrift hergibt; sonst
    // blendet „+ Anschrift" sie ein.
    const [firmaStrasse, setFirmaStrasse] = React.useState(vorbelegung.strasse ?? "");
    const [firmaPlz, setFirmaPlz] = React.useState(vorbelegung.plz ?? "");
    const [firmaOrt, setFirmaOrt] = React.useState(vorbelegung.ort ?? "");
    const [anschriftOffen, setAnschriftOffen] = React.useState(
        () => !!(vorbelegung.strasse || vorbelegung.plz || vorbelegung.ort),
    );
    const anschriftAusSignatur = !!(vorbelegung.plz || vorbelegung.strasse);
    const anschriftUnsicher = anschriftAusSignatur && erkennung?.signature?.address?.confident === false;
    const [apAn, setApAn] = React.useState(() => vorbelegung.lastName !== "");
    const [apLast, setApLast] = React.useState(vorbelegung.lastName);
    const [apFirst, setApFirst] = React.useState(vorbelegung.firstName);
    const [apEmail, setApEmail] = React.useState(vorbelegung.email);
    const [apTelefon, setApTelefon] = React.useState(vorbelegung.telefon);
    const [apMobil, setApMobil] = React.useState(vorbelegung.mobil);
    // Verantwortlichkeit an der Firma (01.10.2026, statt Freitext „Funktion")
    // — dieselbe Eingabe wie die Vollanlage bei „Verbindung zu Unternehmen".
    // Geteilt zwischen Ansprechpartner der neuen Firma, Ansprechpartner einer
    // bestehenden Firma und der Verbindung des Privatkontakts (C3); es ist
    // immer nur einer der drei Blöcke sichtbar.
    const [verant, setVerantRoh] = React.useState<VerantwortlichkeitValue>(LEERE_VERANTWORTLICHKEIT);
    const verantBeruehrt = React.useRef(false);
    const setVerant = React.useCallback((v: VerantwortlichkeitValue) => {
        // Der Haken-Abgleich der Eingabe meldet sich auch ohne Klick — erst eine
        // geänderte Rollenwahl zählt als Eingabe des Anwenders.
        verantBeruehrt.current = true;
        setVerantRoh(v);
    }, []);
    // T-36: Funktion aus der Signatur — vorgewählt wird nur ein Katalogtreffer.
    const funktionAusSignatur = (vorbelegung.funktion ?? "").trim();
    const { items: verantKatalog } = useVerantwortlichkeiten();
    const signaturVerant = React.useMemo(
        () => verantAusSignatur(funktionAusSignatur, verantKatalog),
        [funktionAusSignatur, verantKatalog],
    );
    React.useEffect(() => {
        if (!signaturVerant || verantBeruehrt.current) return;
        setVerantRoh(signaturVerant);
    }, [signaturVerant]);
    const [pLast, setPLast] = React.useState(vorbelegung.lastName);
    const [pFirst, setPFirst] = React.useState(vorbelegung.firstName);
    const [pEmail, setPEmail] = React.useState(vorbelegung.email);
    const [pEmailArt, setPEmailArt] = React.useState<KommArt>("privat");
    const [pTelefon, setPTelefon] = React.useState(vorbelegung.telefon);
    const [pTelefonArt, setPTelefonArt] = React.useState<KommArt>("privat");
    const [pMobil, setPMobil] = React.useState(vorbelegung.mobil);
    const [pMobilArt, setPMobilArt] = React.useState<KommArt>("privat");
    // C3 — optionale Verbindung des Privatkontakts zu einer Firma. Bewusst nur
    // die beiden Arten aus SYSTEM_FIRMA_BEZIEHUNGSTYPEN, die KEINE Organrolle
    // sind (Annahme A2): Organrollen (GF/Gesellschafter) hängen an der
    // Rechtsform und gehören in die Vollanlage, nicht in eine Schnellmaske.
    const [pFirma, setPFirma] = React.useState<KontaktTreffer | null>(null);
    // Neuer Ansprechpartner bei einer BESTEHENDEN Firma (früher Firma-Zweig,
    // „bestehende wählen"). F.0: Gehört die Absenderdomäne eindeutig zu einer
    // bekannten Firma, steht sie schon drin; bei mehreren bleibt das Feld leer
    // und der Hinweis bittet um die Wahl.
    const [pApFirma, setPApFirmaRoh] = React.useState<KontaktTreffer | null>(() => {
        const dom = erkennung?.companyDomain;
        if (absenderIntern || !dom || dom.ambiguous || dom.companies.length !== 1) return null;
        const f = dom.companies[0];
        return {
            contactId: f.contactId,
            displayName: f.displayName,
            contactType: "Firma",
            art: "firma",
            statusName: null,
            companyId: null,
            companyName: null,
            defaultEmail: null,
            contactNbrInt: null,
        };
    });
    const apFirmaBeruehrt = React.useRef(false);
    const setPApFirma = React.useCallback((t: KontaktTreffer | null) => {
        apFirmaBeruehrt.current = true;
        setPApFirmaRoh(t);
    }, []);
    const apBeiFirma = neuTyp === "privat" && pApFirma != null;

    // T-333 (Dirk 15.10.2026): Die Signatur nannte „PLANPROTECT AG", die
    // Domäne war unbekannt — die Anlage startete im Firma-Zweig und legte die
    // längst vorhandene „Planprotect AG" ein zweites Mal an. Deshalb wird die
    // Firma der Signatur über ihren NAMEN gesucht (Groß/Klein und Schreibweise
    // der Rechtsform egal): Genau ein Treffer belegt „Ansprechpartner bei
    // Firma" vor, mehrere stehen zur Auswahl — in beiden Fällen im
    // Personen-Zweig statt einer Neuanlage. Ohne Treffer bleibt alles wie bisher.
    const [signaturTreffer, setSignaturTreffer] = React.useState<KontaktTreffer[]>([]);
    React.useEffect(() => {
        if (!signaturFirmaSuchen) return;
        const ctrl = new AbortController();
        sucheFirmaNachNamen(signaturFirma, ctrl.signal)
            .then((liste) => {
                if (ctrl.signal.aborted || liste.length === 0) return;
                setSignaturTreffer(liste);
                if (!typBeruehrt.current) setNeuTypRoh("privat");
                if (liste.length === 1 && !apFirmaBeruehrt.current) setPApFirmaRoh(liste[0]);
            })
            .catch(() => {
                /* Ohne Antwort bleibt es bei der Firmenanlage — wie vor T-333. */
            });
        return () => ctrl.abort();
    }, [signaturFirmaSuchen, signaturFirma]);
    const signaturAuswahl = !pApFirma && signaturTreffer.length > 1 ? signaturTreffer : [];

    // Rechtsform der gewählten BESTEHENDEN Firma — sie entscheidet wie in der
    // Vollanlage, welche Organrollen die Verantwortlichkeit anbietet. Der
    // Suchtreffer kennt sie nicht, darum ein Blick in den Kontakt.
    const gewaehlteFirmaId = neuTyp === "privat" ? (pApFirma?.contactId ?? pFirma?.contactId ?? null) : null;
    const [firmaRechtsform, setFirmaRechtsform] = React.useState<{ id: number; legalFormId?: number } | null>(null);
    React.useEffect(() => {
        if (!gewaehlteFirmaId) return;
        let aktiv = true;
        (async () => {
            try {
                const res = await fetch(apiUrl(`contact/${gewaehlteFirmaId}`));
                const c = res.ok ? await res.json() : null;
                const lf = Number(c?.legalForm?.id ?? c?.legalFormId);
                if (aktiv) setFirmaRechtsform({ id: gewaehlteFirmaId, legalFormId: lf > 0 ? lf : undefined });
            } catch {
                if (aktiv) setFirmaRechtsform({ id: gewaehlteFirmaId });
            }
        })();
        return () => {
            aktiv = false;
        };
    }, [gewaehlteFirmaId]);
    const verantLegalFormId =
        neuTyp === "firma"
            ? Number(legalFormId) > 0
                ? Number(legalFormId)
                : undefined
            : firmaRechtsform && firmaRechtsform.id === gewaehlteFirmaId
              ? firmaRechtsform.legalFormId
              : undefined;

    // FO/BO (OwnershipSection füllt vor) — geteilt zwischen Firma- und Privat-Zweig.
    const [own, setOwn] = React.useState<FormState>({});
    const ownership: AssignOwnership = React.useMemo(
        () => ({
            foUserId: typeof own.foUserId === "string" ? own.foUserId : undefined,
            boType: typeof own.boType === "string" ? own.boType : undefined,
            boUserId: typeof own.boUserId === "string" ? own.boUserId : undefined,
            boTeamId: typeof own.boTeamId === "string" ? own.boTeamId : undefined,
        }),
        [own],
    );
    const updateOwn = React.useCallback(
        (k: string, v: string) => setOwn((o) => ({ ...o, [k]: v })),
        [],
    );

    // ── C7: Dublettenwarnung auch in der Schnellanlage ──────────────────────
    // Drei getrennte Prüfungen, weil drei getrennte Datensätze entstehen können.
    // `enabled` schaltet sie ab, solange der jeweilige Block gar nicht sichtbar
    // ist — sonst liefe eine Suche für einen Zweig, den niemand ausgefüllt hat.
    // `requireFirstName: false` bei den Personen (Annahme A14): aus einer Mail
    // kennt man oft nur den Nachnamen.
    const dubFirma = useDuplicateCheck({
        isCompany: true,
        lastName: firmenname,
        firstName: "",
        birthDate: "",
        email: firmaEmail,
        enabled: neuOffen && neuTyp === "firma",
    });
    const dubAp = useDuplicateCheck({
        isCompany: false,
        lastName: apLast,
        firstName: apFirst,
        birthDate: "",
        email: apEmail,
        requireFirstName: false,
        enabled: neuOffen && neuTyp === "firma" && apAn,
    });
    const dubPrivat = useDuplicateCheck({
        isCompany: false,
        lastName: pLast,
        firstName: pFirst,
        birthDate: "",
        email: pEmail,
        requireFirstName: false,
        enabled: neuOffen && neuTyp === "privat",
    });

    // Die ABSENDERadresse selbst — für Weg 1 und den Kopf. Die Vorbelegung der
    // Neuanlage lässt eine interne Adresse dagegen weg (T-43).
    const email = prefillEmail(senderEmail);
    // B6: eigene Domäne → die Adresse wird NIE übernommen (der Payload-Builder
    // gibt dann null zurück). Der Hinweis macht das im Dialog sichtbar.
    const adresseIstIntern = istInterneAdresse(email, interneDomaenen);

    // A1 — genau EIN Weg ist aktiv. „Der zuletzt bediente gewinnt" wäre beim
    // Absenden nicht sichtbar und produzierte falsche Zuweisungen.
    const oeffneNeu = () => {
        if (treffer) setVerworfen(treffer.displayName);
        setTreffer(null);
        setSelMeta(null);
        setNeuOffen(true);
        setError(null);
    };
    const waehleTreffer = (t: KontaktTreffer | null) => {
        setTreffer(t);
        setNeuOffen(false);
        setVerworfen(null);
        setError(null);
        setSelMeta(null);
        if (!t) return;
        // A15: an Nicht-Personen lehnt der Server ein `kind` mit 400 ab.
        // T-27 (PO 01.10.2026, löst die feste „privat"-Vorbelegung aus T-26 ab):
        // geschäftlich bei Status „Sonstige" (also auch beim Ansprechpartner),
        // privat bei Interessent oder Kunde. Die Vorbelegung ist umschaltbar
        // und gilt zugleich für einen neuen Vorgang (privat → Person,
        // geschäftlich → Firma). An einer Person mit Firmenbezug entscheidet
        // dieselbe Regel wie der Erstell-Flow „Neuer Vorgang" (vorgangFuer.ts),
        // damit beide Wege gleich vorbelegen.
        const firmenDesTreffers = firmenVon(t);
        setAdresseArt(
            t.contactType === "Person" && firmenDesTreffers.length > 0
                ? vorgangStartetMitFirma(kisGruppe(t), firmenDesTreffers)
                    ? "geschaeftlich"
                    : "privat"
                : adresseArtVorbelegung(t),
        );
        // Beim WEITEREN Kontakt gehört die Absenderadresse meist dem ersten
        // (dem Absender) — dann nicht ungefragt hinterlegen.
        setAdresseHinterlegen(modus !== "zusaetzlich");
        const ersteFirma = firmenDesTreffers[0];
        setAdresseFirmaWahl(ersteFirma ? String(ersteFirma.id) : ANDERE_FIRMA);
        setAdresseFirmaSuche(null);
        setSelMetaLoading(true);
        void contactCommunicationService
            .loadMeta(t.contactId)
            .then(setSelMeta)
            .catch(() => setSelMeta({ contactType: null, communications: [], canWrite: false }))
            .finally(() => setSelMetaLoading(false));
    };

    const fertig = (ergebnis: MailZuweisungErgebnis) => {
        closeWindow(windowId);
        onResolved(ergebnis);
    };

    const trefferFirmen = treffer ? firmenVon(treffer) : [];
    // T-8: Für einen neuen Vorgang zählt „privat | geschäftlich" nur an einer
    // Person mit Firmenbezug — und nur dort, wo überhaupt ein Vorgang entstehen kann.
    const vorgangWahlSichtbar =
        !!treffer &&
        treffer.contactType === "Person" &&
        trefferFirmen.length > 0 &&
        modus !== "nur-anlegen" &&
        modus !== "zusaetzlich";
    const vorgangPersonLabel = treffer?.displayName ?? "Person";

    // T-27: Für die Adresse zählt die Wahl nur, wenn sie wirklich hinterlegt
    // werden kann — sonst wirkt eine (geschäftliche) Vorbelegung dort nicht.
    const adresseWahlSichtbar =
        !!treffer &&
        treffer.contactType === "Person" &&
        adresseHinterlegen &&
        !selMetaLoading &&
        !!email &&
        !adresseIstIntern &&
        !hasSameEmail(selMeta?.communications, email) &&
        !!selMeta?.canWrite;
    const adresseFirma: KontaktRef | null =
        adresseArt !== "geschaeftlich"
            ? null
            : adresseFirmaWahl === ANDERE_FIRMA || adresseFirmaWahl === ""
              ? adresseFirmaSuche
                  ? { id: adresseFirmaSuche.contactId, displayName: adresseFirmaSuche.displayName }
                  : null
              : (() => {
                    const f = trefferFirmen.find((x) => String(x.id) === adresseFirmaWahl);
                    return f ? { id: f.id, displayName: f.name } : null;
                })();
    // EINE Frage für beides: sichtbar, sobald Adresse ODER Vorgang davon abhängen.
    const artWahlSichtbar = adresseWahlSichtbar || vorgangWahlSichtbar;
    const geschaeftlichFirma = artWahlSichtbar && adresseArt === "geschaeftlich" ? adresseFirma : null;

    /**
     * Weg 1: Absender-Adresse best-effort am Kontakt hinterlegen (die Zuweisung
     * selbst darf daran nie scheitern), dann das Ergebnis liefern.
     */
    const zuweisenBestand = async (weiter: "zuweisen" | "vorgang") => {
        if (!treffer || busy) return;
        setBusy(true);
        setError(null);
        let uebernommen = false;
        if (adresseHinterlegen && email && selMeta?.canWrite) {
            const payload = buildAttachEmailPayload({
                value: email,
                label:
                    treffer.contactType === "Person"
                        ? adresseArt === "geschaeftlich"
                            ? "Geschäftlich"
                            : "Privat"
                        : undefined,
                existingComms: selMeta.communications,
                interneDomaenen,
                // C8: die Firma wandert nur mit, wenn die Adresse auch wirklich
                // als geschäftlich markiert ist — sonst antwortet der Server 400.
                companyId:
                    treffer.contactType === "Person" && adresseArt === "geschaeftlich"
                        ? (adresseFirma?.id ?? null)
                        : null,
            });
            if (payload) {
                try {
                    await contactCommunicationService.attachEmail(treffer.contactId, payload);
                    uebernommen = true;
                } catch (error) {
                    // best-effort: die Zuweisung darf nicht am Mail-Nachtrag scheitern.
                    reportMailError({
                        area: "mail",
                        action: "assign",
                        error,
                        fallbackMessage: "Die Absenderadresse konnte nicht am Kontakt gespeichert werden.",
                    });
                }
            }
        }
        fertig({
            kontakt: { id: treffer.contactId, displayName: treffer.displayName },
            // Die Firma kommt nur mit, wenn „geschäftlich" bei einer Firma gewählt
            // ist (C8/T-27) — bei mehreren Firmen die GEWÄHLTE. Ein neuer Vorgang
            // läuft dann auf sie (T-8), bei „privat" auf die Person.
            firma: geschaeftlichFirma,
            // Nur gesetzt, wenn die Frage für einen Vorgang überhaupt stand —
            // `undefined` heißt „nicht gefragt", dann entscheidet der Erstell-Flow
            // nach seiner Regel (vorgangFuer.ts).
            vorgangAufFirma: vorgangWahlSichtbar ? !!geschaeftlichFirma : undefined,
            weiter,
            adresseUebernommen: uebernommen,
            angelegt: [],
        });
    };

    const firmaWerte: FirmaWerte = {
        name: firmenname,
        gesellschaftsform,
        legalFormId,
        // Die Absenderadresse steht IMMER genau einmal in der Maske: mit
        // AP-Block bei ihm, ohne ihn bei der Firma.
        email: apAn ? "" : firmaEmail,
        // Firmenzeilen tragen bewusst KEIN kind — der Server nullt es an
        // Nicht-Personen ohnehin (sanitizeCommunicationKindCompany).
        telefon: firmaTelefon,
        mobil: firmaMobil,
        strasse: firmaStrasse,
        plz: firmaPlz,
        ort: firmaOrt,
    };
    // Die gewählte Firma ist zugleich das Ziel der geschäftlichen Zeilen (C8) —
    // buildPrivatPayload bekommt sie unten über `firmaVerbindung`.
    const privatWerte: PrivatWerte = {
        lastName: pLast,
        firstName: pFirst,
        email: pEmail,
        emailArt: pEmailArt,
        telefon: pTelefon,
        telefonArt: pTelefonArt,
        mobil: pMobil,
        mobilArt: pMobilArt,
    };

    /** Die Eingaben von Weg 2 — für die Schnellanlage wie für die Vollanlage. */
    const anlageEingabe = (): AnlageEingabe =>
        neuTyp === "firma"
            ? {
                  typ: "firma",
                  status: statusWahl,
                  firma: firmaWerte,
                  ap: apAn
                      ? {
                            lastName: apLast,
                            firstName: apFirst,
                            email: apEmail,
                            telefon: apTelefon,
                            mobil: apMobil,
                            verant,
                        }
                      : null,
                  ownership,
              }
            : pApFirma
              ? {
                    // Neue Person als Ansprechpartner der gewählten Firma —
                    // derselbe Weg wie früher „Firma → bestehende wählen".
                    typ: "ap",
                    firma: { id: pApFirma.contactId, displayName: pApFirma.displayName },
                    ap: {
                        lastName: pLast,
                        firstName: pFirst,
                        email: pEmail,
                        telefon: pTelefon,
                        mobil: pMobil,
                        verant,
                    },
                }
              : {
                  typ: "privat",
                  status: statusWahl,
                  person: privatWerte,
                  firmaVerbindung: pFirma
                      ? {
                            firmaId: pFirma.contactId,
                            firmaName: pFirma.displayName,
                            verant,
                        }
                      : null,
                  ownership,
              };

    const anlageDeps = {
        postContact,
        attachMenschen: (items: Parameters<typeof attachMenschen>[0]) => attachMenschen(items),
        postRelation,
        setzeArbeitgeber: (personId: number, firmaId: number) => arbeitgeberSetzen(personId, firmaId),
        typeIds,
        // T-43/B6: eine interne Adresse wird auch bei der Neuanlage nie gespeichert.
        interneDomaenen,
    };

    /** Weg 2: anlegen und dann das Ergebnis liefern. */
    const anlegenUndZuweisen = async (
        weiter: "zuweisen" | "vorgang",
        vonStand: AnlageStand = stand,
        eingabe: AnlageEingabe = anlageEingabe(),
    ) => {
        if (busy) return;
        setBusy(true);
        setError(null);
        const res = await legeKontaktAn(eingabe, vonStand, anlageDeps);
        setStand(res.stand);
        if ("fehler" in res) {
            setError(res.fehler);
            setBusy(false);
            return;
        }
        fertig({ ...res.ergebnis, weiter });
    };

    // ── Vollanlage aus der Schnellanlage (User 02.10.2026) ──────────────────
    // „+ Weitere Informationen erfassen" öffnet die volle Anlage mit diesen
    // Eingaben. Ihr Knopf heißt beim Zuweisen „Weiter" (danach geht es zum
    // Vorgang wie hier), sonst „Übernehmen". Sie legt den Hauptkontakt an; den
    // Rest (Ansprechpartner, Verbindung zur Firma) erledigt `legeKontaktAn`
    // mit ihm im `stand` — nichts entsteht doppelt. Solange sie offen ist,
    // ruhen die Knöpfe hier, damit nicht parallel ein zweiter Kontakt entsteht.
    const [vollanlageOffen, setVollanlageOffen] = React.useState(false);
    const weiterNachVollanlage: "zuweisen" | "vorgang" = modus === "zuweisen" ? "vorgang" : "zuweisen";
    const vollanlage = mailVollanlageVorbereiten(anlageEingabe(), statusFelder, interneDomaenen);
    const vollAngelegt = (contactId: number, name: string) => {
        setVollanlageOffen(false);
        let eingabe = anlageEingabe();
        // Ein angehakter, aber namenloser Ansprechpartner hält hier sonst die
        // fertige Firma auf — die Schnellanlage ließe so gar nicht speichern.
        if (eingabe.typ === "firma" && eingabe.ap && !apPflichtOk({ lastName: eingabe.ap.lastName })) {
            eingabe = { ...eingabe, ap: null };
        }
        const neuerStand = standNachVollanlage(eingabe, stand, contactId, name);
        // Sofort merken: Scheitert ein Folgeschritt, legt „Erneut versuchen"
        // den Kontakt nicht noch einmal an.
        setStand(neuerStand);
        void anlegenUndZuweisen(weiterNachVollanlage, neuerStand, eingabe);
    };

    // C6 — Pflichtfelder, abgeglichen mit den Vollanlagen. E-Mail ist NIRGENDS Pflicht.
    const neuOk =
        neuTyp === "firma"
            ? firmaPflichtOk(firmaWerte, ownership) && (!apAn || apPflichtOk({ lastName: apLast }))
            : apBeiFirma
              ? apPflichtOk({ lastName: pLast })
              : privatPflichtOk(privatWerte) &&
                // Ohne Verantwortlichkeit und ohne „Mensch im Unternehmen" gäbe es
                // nichts, was die gewählte Firma mit der Person verbindet.
                (!pFirma || hatVerbindungsAngabe(verant));

    // A11: Der gewählte Status WIRKT nicht, wenn die Person Ansprechpartner
    // einer bestehenden Firma wird — sie bekommt serverseitig zwingend
    // „Verantwortung fürs Unternehmen", die Firma behält ihren Status.
    const statusWirkt = !apBeiFirma;

    /**
     * „Verantwortlichkeit" — die Eingabe der Vollanlage („Verbindung zu
     * Unternehmen"): Typ, Katalog-Dropdown, Haken „Mensch im Unternehmen".
     * Darunter, was beim Speichern tatsächlich entsteht.
     */
    const verantFeld = (
        idBase: string,
        firmaName: string,
        fall: Parameters<typeof verbindungsHinweis>[1],
    ) => {
        const h = verbindungsHinweis(verant, fall);
        return (
            <div style={S.verant}>
                <VerantwortlichkeitInput
                    idBase={idBase}
                    value={verant}
                    onChange={setVerant}
                    legalFormId={verantLegalFormId}
                    // Die Firma ist oben gewählt bzw. wird angelegt.
                    hideExternalCompany
                />
                {funktionAusSignatur && (
                    <div style={S.note}>
                        {signaturVerant
                            ? `Verantwortlichkeit aus der Signatur („${funktionAusSignatur}", unsicher) — bitte prüfen.`
                            : `Die Signatur nennt „${funktionAusSignatur}" — das steht so nicht im Katalog. Bitte passend wählen oder unter „Sonstiges" eintragen.`}
                    </div>
                )}
                <div style={S.note}>
                    {h.verbindung ? (
                        <>
                            Verbindung zu <strong>{firmaName}</strong>: {h.verbindung}.{" "}
                        </>
                    ) : (
                        <>Bitte eine Verantwortlichkeit wählen oder „Mensch im Unternehmen" ankreuzen. </>
                    )}
                    {h.status}.
                    {fall.typ === "privat" && istMenschImUnternehmen(verant) && (
                        <> <strong>{firmaName}</strong> wird als Arbeitgeber eingetragen.</>
                    )}
                </div>
            </div>
        );
    };

    const nurAnlegen = modus === "nur-anlegen";
    // PO 28.09.2026 — ein WEITERER Kontakt zu einer schon zugewiesenen Mail:
    // kein Vorgangs-Ausgang, kein „ohne Kontakt", kein Archivieren.
    const zusaetzlich = modus === "zusaetzlich";
    const knoepfe = fusszeileFuer({ treffer: treffer != null, neuOffen, neuOk, busy, modus });
    // T-27: „Weiter" ohne Treffer und ohne vollständige Anlage fragt nach.
    const [ohneKontaktFrage, setOhneKontaktFrage] = React.useState(false);

    const ausfuehren = (weiter: "zuweisen" | "vorgang") => {
        if (treffer) return void zuweisenBestand(weiter);
        return void anlegenUndZuweisen(weiter);
    };
    const knopfGeklickt = (id: "nur" | "weiter" | "einzig") => {
        if (id === "einzig") return ausfuehren("zuweisen");
        if (id === "nur") return ausfuehren("zuweisen");
        if (treffer || (neuOffen && neuOk)) return ausfuehren("vorgang");
        setOhneKontaktFrage(true);
    };

    return (
        <div style={S.root}>
            <div style={S.head}>
                <div style={S.title}>
                    {nurAnlegen ? "Kontakt anlegen" : zusaetzlich ? "Weiteren Kontakt zuweisen" : "Kontakt zuweisen"}
                </div>
                <div style={S.hint}>
                    {markierterText != null ? (
                        // T-36: Anlage aus einer Markierung im Mailtext.
                        erkennung?.signature ? (
                            <>Aus der Markierung in der Mail übernommen — bitte die Felder prüfen.</>
                        ) : (
                            <>In der Markierung war nichts eindeutig erkennbar — bitte die Daten eintragen.</>
                        )
                    ) : zusaetzlich && aktuelleZuweisung ? (
                        <>
                            Bereits zugewiesen: <strong>{aktuelleZuweisung.displayName}</strong>. Die Mail
                            wird zusätzlich dem Kontakt zugewiesen, den du hier wählst oder anlegst.
                        </>
                    ) : gegenstand ? (
                        gegenstand.kopf
                    ) : (
                        kopfzeile(email, aktuelleZuweisung ?? null, erkennung ?? null)
                    )}
                </div>
            </div>

            <div style={S.body}>
                {markierterText != null && !erkennung?.signature && markierterText.trim() && (
                    <pre style={S.markierung} aria-label="Markierter Text">
                        {markierterText.trim()}
                    </pre>
                )}
                {/* ── Weg 1 ─────────────────────────────────────────────── */}
                <div style={S.card}>
                    <div style={S.wegTitel}>Bestehenden Kontakt suchen</div>
                    <KontaktSuchfeld
                        label="Kontakt"
                        name="mca-suche"
                        autoFocus
                        value={treffer}
                        onChange={waehleTreffer}
                    />
                    {treffer && (
                        <>
                            {selMetaLoading ? (
                                <div style={S.note}>Kontaktdaten werden geladen…</div>
                            ) : gegenstand ? null : !email ? (
                                <div style={S.note}>
                                    Diese Mail trägt keine auswertbare Absenderadresse — die Mail wird
                                    dem Kontakt trotzdem zugewiesen.
                                </div>
                            ) : adresseIstIntern ? (
                                // B6: eigene Domäne — die Adresse gehört einem Kollegen,
                                // nicht dem Kunden. Sie wird nie hinterlegt; die MAIL
                                // landet trotzdem im Schriftverkehr des Kontakts (B3).
                                <div style={S.note}>
                                    <strong>{email}</strong> ist eine eigene Adresse und wird deshalb
                                    nicht beim Kontakt hinterlegt. Die Mail selbst wird dem Kontakt
                                    zugewiesen.
                                </div>
                            ) : hasSameEmail(selMeta?.communications, email) ? (
                                <div style={S.note}>
                                    Die Adresse <strong>{email}</strong> ist bereits hinterlegt. Die
                                    Mail wird trotzdem zugewiesen.
                                </div>
                            ) : !selMeta?.canWrite ? (
                                <div style={S.note}>
                                    Kein Schreibrecht — <strong>{email}</strong> wird nicht hinterlegt;
                                    zuweisen kannst du trotzdem.
                                </div>
                            ) : (
                                <>
                                    <label style={S.check}>
                                        <input
                                            type="checkbox"
                                            checked={adresseHinterlegen}
                                            onChange={(e) => setAdresseHinterlegen(e.target.checked)}
                                        />{" "}
                                        <span>
                                            E-Mail-Adresse <strong>{email}</strong>{" "}
                                            {hasDefaultEmail(selMeta?.communications)
                                                ? "als weiteren Kommunikationsweg"
                                                : "als Standard"}{" "}
                                            beim Kontakt hinterlegen
                                        </span>
                                    </label>
                                </>
                            )}
                            {/* T-8 + T-27 — EINE Frage „privat | geschäftlich": sie bestimmt
                                die Art der Absenderadresse und, auf wen ein neuer Vorgang
                                läuft. Bei geschäftlich steht die Firma da — eine fest, mehrere
                                zur Auswahl. A15: nur an Personen. */}
                            {artWahlSichtbar && (
                                <div style={S.vorgangWahl}>
                                    {vorgangWahlSichtbar && (
                                        <div style={S.note}>
                                            Neuer Vorgang: privat auf die Person, geschäftlich auf die Firma.
                                            {gegenstand ? gegenstand.name : "Die Mail"} wird in jedem Fall{" "}
                                            <strong>{vorgangPersonLabel}</strong>{" "}
                                            zugewiesen.
                                        </div>
                                    )}
                                    <StatusSegmented
                                        ariaLabel="Privat oder geschäftlich"
                                        value={adresseArt === "geschaeftlich" ? "geschäftlich" : "privat"}
                                        options={["privat", "geschäftlich"]}
                                        onChange={(v) =>
                                            setAdresseArt(v === "geschäftlich" ? "geschaeftlich" : "privat")
                                        }
                                    />
                                    {adresseArt === "geschaeftlich" && (
                                        <>
                                            {trefferFirmen.length === 1 && adresseFirmaWahl !== ANDERE_FIRMA && (
                                                <div style={S.note}>
                                                    Firma: <strong>{trefferFirmen[0].name}</strong>
                                                    {adresseWahlSichtbar && (
                                                        <>
                                                            {" "}
                                                            <button
                                                                type="button"
                                                                style={S.mini}
                                                                onClick={() => setAdresseFirmaWahl(ANDERE_FIRMA)}
                                                            >
                                                                Andere Firma suchen …
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                            {trefferFirmen.length > 1 && (
                                                <LabeledSelect
                                                    label="Firma"
                                                    name="mca-adresse-firma"
                                                    value={adresseFirmaWahl}
                                                    onChange={(v) => {
                                                        setAdresseFirmaWahl(v);
                                                        if (v !== ANDERE_FIRMA) setAdresseFirmaSuche(null);
                                                    }}
                                                    options={[
                                                        ...trefferFirmen.map((f) => ({
                                                            value: String(f.id),
                                                            label: f.name,
                                                        })),
                                                        // Eine fremde Firma gibt es nur für die Adresse (C8).
                                                        ...(adresseWahlSichtbar
                                                            ? [{ value: ANDERE_FIRMA, label: "Andere Firma suchen …" }]
                                                            : []),
                                                    ]}
                                                />
                                            )}
                                            {adresseWahlSichtbar &&
                                                (trefferFirmen.length === 0 || adresseFirmaWahl === ANDERE_FIRMA) && (
                                                    <KontaktSuchfeld
                                                        label="Firma"
                                                        name="mca-adresse-firma-suche"
                                                        placeholder="Firma suchen …"
                                                        nurTyp="firma"
                                                        value={adresseFirmaSuche}
                                                        onChange={setAdresseFirmaSuche}
                                                        hint={
                                                            adresseFirmaSuche
                                                                ? "Die Adresse wird dieser Firma zugeordnet; eine Verbindung entsteht dadurch nicht."
                                                                : "Optional — ohne Firma wird die Adresse nur als geschäftlich markiert."
                                                        }
                                                    />
                                                )}
                                            {trefferFirmen.length === 1 && adresseFirmaWahl === ANDERE_FIRMA && (
                                                <button
                                                    type="button"
                                                    style={S.mini}
                                                    onClick={() => {
                                                        setAdresseFirmaWahl(String(trefferFirmen[0].id));
                                                        setAdresseFirmaSuche(null);
                                                    }}
                                                >
                                                    Doch {trefferFirmen[0].name}
                                                </button>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* ── Weg 2 ─────────────────────────────────────────────── */}
                {!neuOffen ? (
                    <button type="button" style={S.aufklapp} onClick={oeffneNeu}>
                        + Neuen Kontakt erstellen
                    </button>
                ) : (
                    <div style={S.card}>
                        <div style={S.wegTitel}>Neuen Kontakt erstellen</div>
                        {verworfen && (
                            <div style={S.note}>
                                Die Auswahl <strong>{verworfen}</strong> wurde verworfen.
                            </div>
                        )}
                        {/* T-43/B6: Die Mail kommt von einem Kollegen — seine Daten
                            gehören nicht in den neuen Kundenkontakt. */}
                        {absenderIntern && (
                            <div style={S.note}>
                                Die Mail kommt von einer Kollegin oder einem Kollegen
                                {email ? (
                                    <>
                                        {" "}(<strong>{email}</strong>)
                                    </>
                                ) : null}
                                . Name und Adresse werden deshalb nicht übernommen — bitte die Daten
                                der Kundin oder des Kunden eintragen. Eine interne Adresse wird nicht
                                gespeichert.
                            </div>
                        )}

                        {/* T-27 (PO 01.10.2026): Reihenfolge wie in der Anlage —
                            zuerst der Status (bei „Sonstige" Bereich/Rolle), dann
                            Firma | Privat. Die „Für wen?"-Bezüge entfallen in der
                            Schnellanlage; sie werden in der Akte nachgetragen. */}
                        <KontaktStatusSchritt
                            f={statusF}
                            set={statusSet}
                            typ={(neuTyp === "firma" ? "firma" : "person") as KontaktTyp}
                            onTyp={(t) => setNeuTyp(t === "firma" ? "firma" : "privat")}
                            coIntern={hasPermission("cointern:read")}
                            ohneBezuege
                        />

                        {/* A11 — als Ansprechpartner einer bestehenden Firma wirkt der Status nicht. */}
                        {!statusWirkt && (
                            <div style={S.note}>
                                Der Status der Firma{" "}
                                <strong>{pApFirma?.displayName ?? "…"}</strong> bleibt unverändert.
                                Der neue Ansprechpartner bekommt den Status{" "}
                                <strong>Verantwortung fürs Unternehmen</strong>.
                            </div>
                        )}

                        {neuTyp === "firma" ? (
                            <>
                                    <>
                                        <LabeledInput
                                            label="Firmenname"
                                            name="mca-firma-name"
                                            required
                                            value={firmenname}
                                            onChange={setFirmenname}
                                            placeholder="Muster GmbH"
                                        />
                                        <LegalFormSelect
                                            label="Rechtsform"
                                            name="mca-rechtsform"
                                            required
                                            value={gesellschaftsform}
                                            onChange={(v, id) => {
                                                rechtsformBeruehrt.current = true;
                                                setGesellschaftsform(v);
                                                setLegalFormId(id ?? "");
                                            }}
                                        />
                                        {!apAn && (
                                            <EmailInput
                                                label="E-Mail"
                                                name="mca-firma-email"
                                                value={firmaEmail}
                                                onChange={setFirmaEmail}
                                            />
                                        )}
                                        {/* C4: Telefon/Mobil aus der Signatur (A5). */}
                                        <div style={S.row2}>
                                            <PhoneInput label="Telefon" name="mca-firma-phone" kind="landline" value={firmaTelefon} onChange={setFirmaTelefon} geschaeftlich />
                                            <PhoneInput label="Mobil" name="mca-firma-mobile" kind="mobile" value={firmaMobil} onChange={setFirmaMobil} geschaeftlich />
                                        </div>
                                        {/* T-36: Unternehmenssitz, vorbelegt aus der Signatur. */}
                                        {anschriftOffen ? (
                                            <>
                                                <LabeledInput label="Straße" name="mca-firma-strasse" value={firmaStrasse} onChange={setFirmaStrasse} />
                                                <div style={S.row2}>
                                                    <LabeledInput label="PLZ" name="mca-firma-plz" value={firmaPlz} onChange={setFirmaPlz} />
                                                    <LabeledInput label="Ort" name="mca-firma-ort" value={firmaOrt} onChange={setFirmaOrt} />
                                                </div>
                                                {anschriftAusSignatur && (
                                                    <div style={S.note}>
                                                        Anschrift aus der Signatur übernommen
                                                        {anschriftUnsicher ? " (unsicher) — bitte prüfen." : "."}
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <button type="button" style={S.mini} onClick={() => setAnschriftOffen(true)}>
                                                + Anschrift
                                            </button>
                                        )}
                                        <OwnershipSection formFields={own} updateField={updateOwn} />
                                        <DuplicateWarningPanel results={dubFirma.results} loading={dubFirma.loading} />
                                    </>

                                {/* F.7 — Ansprechpartner-Block (C5) */}
                                <label style={S.check}>
                                    <input
                                        type="checkbox"
                                        checked={apAn}
                                        onChange={(e) => setApAn(e.target.checked)}
                                    />{" "}
                                    <span>Ansprechpartner zu dieser Firma anlegen</span>
                                </label>
                                {apAn && (
                                    <div style={S.sub}>
                                        <div style={S.row2}>
                                            <LabeledInput label="Vorname" name="mca-ap-first" value={apFirst} onChange={setApFirst} />
                                            <LabeledInput label="Nachname" name="mca-ap-last" required value={apLast} onChange={setApLast} />
                                        </div>
                                        <EmailInput
                                            label="E-Mail (geschäftlich)"
                                            name="mca-ap-email"
                                            value={apEmail}
                                            onChange={setApEmail}
                                        />
                                        {/* C4: Ein Ansprechpartner hängt an der Firma — seine
                                            Nummern sind immer geschäftlich, deshalb keine Wahl. */}
                                        <div style={S.row2}>
                                            <PhoneInput label="Telefon (geschäftlich)" name="mca-ap-phone" kind="landline" value={apTelefon} onChange={setApTelefon} geschaeftlich zentrale={firmaTelefon} />
                                            <PhoneInput label="Mobil (geschäftlich)" name="mca-ap-mobile" kind="mobile" value={apMobil} onChange={setApMobil} geschaeftlich />
                                        </div>
                                        {verantFeld("mca-ap-verant", firmenname.trim() || "der Firma", { typ: "ap" })}
                                        <div style={S.note}>
                                            Betreuung (FO/BO) und Firmenbezug erbt der Ansprechpartner
                                            von der Firma. Geschäftliche Adressen und Nummern werden{" "}
                                            <strong>{firmenname.trim() || "der Firma"}</strong>{" "}
                                            zugeordnet.
                                        </div>
                                        <DuplicateWarningPanel results={dubAp.results} loading={dubAp.loading} />
                                    </div>
                                )}
                            </>
                        ) : (
                            <>
                                {/* Neuer Ansprechpartner bei einer BESTEHENDEN Firma —
                                    früher der Umschalter „bestehende wählen" im
                                    Firma-Zweig. F.0: die Domänen-Firma steht schon drin.
                                    Schließt die reine Verbindung (C3) aus. */}
                                {!pFirma && (
                                    <KontaktSuchfeld
                                        label="Ansprechpartner bei Firma (optional)"
                                        name="mca-p-ap-firma"
                                        placeholder="Firma suchen …"
                                        nurTyp="firma"
                                        value={pApFirma}
                                        onChange={setPApFirma}
                                        hint={
                                            !pApFirma && erkennung?.companyDomain?.ambiguous
                                                ? `@${erkennung.companyDomain.domain} gehört zu mehreren Firmen — bitte die richtige wählen.`
                                                : pApFirma || signaturAuswahl.length > 0
                                                  ? undefined
                                                  : "Optional — für einen neuen Ansprechpartner einer bereits angelegten Firma."
                                        }
                                    />
                                )}
                                {/* T-333: Dubletten im Bestand — Ort und Nummer machen
                                    sie unterscheidbar; gewählt wird hier, nicht neu angelegt. */}
                                {!pFirma && signaturAuswahl.length > 0 && (
                                    <div style={S.signaturWahl}>
                                        <div style={S.note}>
                                            „{signaturFirma}“ gibt es in KMVS {signaturAuswahl.length}-mal — bitte
                                            die richtige Firma wählen:
                                        </div>
                                        {signaturAuswahl.map((t) => (
                                            <button
                                                key={t.contactId}
                                                type="button"
                                                style={S.signaturWahlZeile}
                                                onClick={() => setPApFirma(t)}
                                            >
                                                <strong>{t.displayName}</strong>
                                                <span style={S.signaturWahlZusatz}>
                                                    {[t.anschrift || "ohne Anschrift", t.contactNbrInt ? `Nr. ${t.contactNbrInt}` : null]
                                                        .filter(Boolean)
                                                        .join(" · ")}
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                                <div style={S.row2}>
                                    <LabeledInput label="Vorname" name="mca-p-first" value={pFirst} onChange={setPFirst} />
                                    <LabeledInput label="Nachname" name="mca-p-last" required value={pLast} onChange={setPLast} />
                                </div>
                                <div style={S.note}>Nur der Nachname ist Pflicht.</div>
                                {apBeiFirma ? (
                                    <>
                                        {/* Wie beim Ansprechpartner der Firma: seine Daten
                                            hängen an der Firma — immer geschäftlich. */}
                                        <EmailInput label="E-Mail (geschäftlich)" name="mca-p-email" value={pEmail} onChange={setPEmail} />
                                        <div style={S.row2}>
                                            <PhoneInput label="Telefon (geschäftlich)" name="mca-p-phone" kind="landline" value={pTelefon} onChange={setPTelefon} geschaeftlich />
                                            <PhoneInput label="Mobil (geschäftlich)" name="mca-p-mobile" kind="mobile" value={pMobil} onChange={setPMobil} geschaeftlich />
                                        </div>
                                        {verantFeld("mca-p-verant", pApFirma?.displayName ?? "der Firma", { typ: "ap" })}
                                        <div style={S.note}>
                                            Betreuung (FO/BO) und Firmenbezug erbt der Ansprechpartner
                                            von <strong>{pApFirma?.displayName}</strong>. Geschäftliche
                                            Adressen und Nummern werden <strong>{pApFirma?.displayName}</strong>{" "}
                                            zugeordnet.
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <EmailInput label="E-Mail" name="mca-p-email" value={pEmail} onChange={setPEmail} />
                                        <StatusSegmented
                                            ariaLabel="Art der E-Mail"
                                            value={pEmailArt === "geschaeftlich" ? "geschäftlich" : "privat"}
                                            options={["privat", "geschäftlich"]}
                                            onChange={(v) => setPEmailArt(v === "geschäftlich" ? "geschaeftlich" : "privat")}
                                        />
                                        {/* C4: Beim Privatkontakt wird je Nummer gefragt — eine
                                            aus der Signatur gelesene Durchwahl ist geschäftlich,
                                            die Handynummer daneben oft nicht. */}
                                        <PhoneInput label="Telefon" name="mca-p-phone" kind="landline" value={pTelefon} onChange={setPTelefon} geschaeftlich={pTelefonArt === "geschaeftlich"} />
                                        <StatusSegmented
                                            ariaLabel="Art der Telefonnummer"
                                            value={pTelefonArt === "geschaeftlich" ? "geschäftlich" : "privat"}
                                            options={["privat", "geschäftlich"]}
                                            onChange={(v) => setPTelefonArt(v === "geschäftlich" ? "geschaeftlich" : "privat")}
                                        />
                                        <PhoneInput label="Mobil" name="mca-p-mobile" kind="mobile" value={pMobil} onChange={setPMobil} geschaeftlich={pMobilArt === "geschaeftlich"} />
                                        <StatusSegmented
                                            ariaLabel="Art der Mobilnummer"
                                            value={pMobilArt === "geschaeftlich" ? "geschäftlich" : "privat"}
                                            options={["privat", "geschäftlich"]}
                                            onChange={(v) => setPMobilArt(v === "geschäftlich" ? "geschaeftlich" : "privat")}
                                        />
                                        {/* C3 — Verbindung zur Firma, optional. */}
                                        <KontaktSuchfeld
                                            label="Verbindung zur Firma"
                                            name="mca-p-firma"
                                            placeholder="Firma suchen …"
                                            nurTyp="firma"
                                            value={pFirma}
                                            onChange={setPFirma}
                                            hint={pFirma ? undefined : "Optional — ohne Firma bleibt es ein reiner Privatkontakt."}
                                        />
                                        {pFirma && (
                                            <>
                                                {verantFeld("mca-p-firma-verant", pFirma.displayName, {
                                                    typ: "privat",
                                                    statusName: statusWahl.statusName,
                                                })}
                                                <div style={S.note}>
                                                    Als geschäftlich markierte Adressen und Nummern werden{" "}
                                                    <strong>{pFirma.displayName}</strong> zugeordnet.
                                                </div>
                                            </>
                                        )}
                                        <OwnershipSection formFields={own} updateField={updateOwn} />
                                    </>
                                )}
                                <DuplicateWarningPanel results={dubPrivat.results} loading={dubPrivat.loading} />
                            </>
                        )}
                        {/* Steht aus einem früheren Versuch schon ein Kontakt
                            (Folgeschritt gescheitert), bliebe die Vollanlage ein
                            zweiter — dann nur noch „Erneut versuchen" unten. */}
                        {!stand.firmaId && !stand.personId && (
                        <VollanlageWeiterKnopf
                            kontakt={vollanlage.kontakt}
                            zusatzFelder={vollanlage.zusatzFelder}
                            eltern={vollanlage.eltern}
                            speichernText={weiterNachVollanlage === "vorgang" ? "Weiter" : "Übernehmen"}
                            disabled={busy}
                            onGeoeffnet={() => setVollanlageOffen(true)}
                            onAbbruch={() => setVollanlageOffen(false)}
                            onCreated={({ contactId, ref }) =>
                                vollAngelegt(contactId, ref.displayName || ref.lastName || "Kontakt")
                            }
                        />
                        )}
                        {vollanlageOffen && (
                            <div style={S.note}>
                                Die vollständige Anlage ist geöffnet — dort geht es weiter. Schließt du
                                sie ohne Speichern, bleibt hier alles, wie es ist.
                            </div>
                        )}
                    </div>
                )}

                {error && (
                    <div style={S.error} role="alert">
                        {error}
                    </div>
                )}
            </div>

            <div style={S.footer}>
                <div style={S.footerHint}>
                    {fussHinweis
                        ? fussHinweis
                        : nurAnlegen
                        ? "Die Mail bleibt unberührt — es wird nur der Kontakt angelegt."
                        : zusaetzlich
                          ? "Die bisherige Zuweisung bleibt; die Mail bleibt, wo sie ist."
                          : gegenstand && (treffer || neuOffen)
                            ? `„Nur …“ ordnet ${gegenstand.name.replace(/^Der /, "den ").replace(/^Die /, "die ")} zu; „Weiter“ legt einen Vorgang an.`
                          : treffer || neuOffen
                            ? "„Nur …“ ordnet die Mail zu und archiviert sie; „Weiter“ legt einen Vorgang an."
                            : "„Weiter“ legt einen Vorgang an — ohne Kontakt, wenn du keinen wählst."}
                </div>
                <div style={S.footerRow}>
                    {knoepfe.map((k) => (
                        <button
                            key={k.id}
                            type="button"
                            style={k.primaer ? S.primary : S.outline}
                            onClick={() => knopfGeklickt(k.id)}
                            disabled={!k.enabled || vollanlageOffen}
                        >
                            {k.primaer && k.id === "einzig" && <PersonAdd20Regular />} {k.label}
                        </button>
                    ))}
                </div>
            </div>
            <ConfirmDialog
                open={ohneKontaktFrage}
                tone="primary"
                title="Ohne Kontakt fortfahren?"
                message="Es ist kein Kontakt gewählt. Der Vorgang wird ohne Kunden angelegt."
                confirmLabel="Ja"
                cancelLabel="Nein"
                icon={<PersonAdd20Regular />}
                onCancel={() => setOhneKontaktFrage(false)}
                onConfirm={() => {
                    setOhneKontaktFrage(false);
                    fertig({
                        kontakt: null,
                        firma: null,
                        weiter: "vorgang",
                        adresseUebernommen: false,
                        angelegt: [],
                    });
                }}
            />
        </div>
    );
};

/** Die Statuszeile im Kopf — sie sagt, WARUM dieser Dialog offen ist. */
function kopfzeile(
    email: string,
    aktuell: { id: number; displayName: string } | null,
    erkennung: MailZuweisungErkennung,
): React.ReactNode {
    if (!email) return "Diese Mail trägt keine auswertbare Absenderadresse.";
    if (aktuell) {
        return (
            <>
                Aktuell zugewiesen: <strong>{aktuell.displayName}</strong>. Passt das nicht, wähle
                unten einen anderen Kontakt.
            </>
        );
    }
    if (erkennung?.intern) {
        return (
            <>
                <strong>{email}</strong> ist eine interne Adresse. Sie wird nicht am Kontakt
                hinterlegt; die Mail wird nur zugeordnet.
            </>
        );
    }
    const dom = erkennung?.companyDomain;
    if (dom && dom.companies.length > 0) {
        return dom.ambiguous ? (
            <>
                <strong>@{dom.domain}</strong> gehört zu mehreren Firmen — bitte die richtige wählen.
            </>
        ) : (
            <>
                <strong>@{dom.domain}</strong> gehört zu <strong>{dom.companies[0].displayName}</strong>{" "}
                — vermutlich ein neuer Ansprechpartner.
            </>
        );
    }
    return (
        <>
            Absender <strong>{email}</strong> wurde nicht erkannt.
        </>
    );
}

const S: Record<string, React.CSSProperties> = {
    root: { display: "flex", flexDirection: "column", height: "100%", background: "#fff", fontSize: 13, color: "#241522" },
    head: { padding: "12px 16px 8px", borderBottom: "1px solid #ece7f0" },
    title: { fontWeight: 700, fontSize: 15, color: AUB },
    hint: { marginTop: 4, color: "#6b5a66", lineHeight: 1.4 },
    body: { flex: 1, overflowY: "auto", padding: "12px 16px", minHeight: 0, display: "flex", flexDirection: "column", gap: 14 },
    card: { borderLeft: `4px solid ${BALKEN}`, paddingLeft: 12, display: "flex", flexDirection: "column", gap: 10 },
    wegTitel: { fontWeight: 700, fontSize: 13, color: AUB },
    sub: { display: "flex", flexDirection: "column", gap: 10, paddingLeft: 10, borderLeft: "2px solid #ece7f0" },
    row2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 },
    note: { color: "#6b5a66", fontSize: 12, lineHeight: 1.4 },
    verant: { display: "flex", flexDirection: "column", gap: 6 },
    vorgangWahl: { display: "flex", flexDirection: "column", gap: 6, paddingTop: 8, borderTop: "1px solid #ece7f0" },
    check: { display: "inline-flex", gap: 6, alignItems: "flex-start", fontSize: 12.5, lineHeight: 1.4, cursor: "pointer" },
    markierung: { margin: 0, padding: "8px 10px", background: "#fbf9fc", border: "1px solid #ece7f0", borderRadius: 6, whiteSpace: "pre-wrap", fontFamily: "inherit", fontSize: 12, color: "#4a3a47", maxHeight: 140, overflowY: "auto" },
    signaturWahl: { display: "flex", flexDirection: "column", gap: 6 },
    signaturWahlZeile: { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2, textAlign: "left", border: "1px solid #ece7f0", background: "#fbf9fc", color: "#241522", borderRadius: 6, padding: "6px 10px", cursor: "pointer", fontSize: 12.5 },
    signaturWahlZusatz: { color: "#6b5a66", fontSize: 12 },
    mini: { alignSelf: "flex-start", border: "none", background: "transparent", color: AUB, cursor: "pointer", padding: "2px 0", fontWeight: 600, fontSize: 12.5 },
    aufklapp: { alignSelf: "flex-start", border: "1px dashed #d9cfd8", background: "#fbf9fc", color: AUB, borderRadius: 8, padding: "8px 14px", cursor: "pointer", fontWeight: 600 },
    error: { color: "#b3261e", background: "#fdecea", border: "1px solid #f5c6cb", borderRadius: 6, padding: "6px 10px" },
    footer: { borderTop: "1px solid #ece7f0", padding: "8px 16px 10px", display: "flex", flexDirection: "column", gap: 6 },
    footerHint: { color: "#6b5a66", fontSize: 11.5 },
    // T-27: höchstens zwei Knöpfe, rechtsbündig in EINER Zeile.
    footerRow: { display: "flex", gap: 8, alignItems: "center", justifyContent: "flex-end", flexWrap: "nowrap" },
    outline: { border: `1px solid ${AUB}`, background: "#fff", color: AUB, borderRadius: 6, padding: "6px 12px", cursor: "pointer", fontWeight: 600 },
    primary: { display: "inline-flex", alignItems: "center", gap: 6, border: `1px solid ${AUB}`, background: AUB, color: "#fff", borderRadius: 6, padding: "6px 14px", cursor: "pointer", fontWeight: 600 },
};

/**
 * Öffnet den Zuweisungs-Flow als Light-Dialog (WindowManager-Fenster): schmal,
 * zentriert, verschiebbar/minimierbar/schließbar (FloatingWindow), mobil Vollbild.
 */
export const openMailContactAssign = (
    openWindow: (config: OpenWindowConfig) => void,
    params: OpenMailContactAssignParams,
): string => {
    const windowId = `mail-assign-${Date.now()}`;
    openWindow({
        id: windowId,
        title:
            params.modus === "nur-anlegen"
                ? "Kontakt anlegen"
                : params.modus === "zusaetzlich"
                  ? "Weiteren Kontakt zuweisen"
                  : "Kontakt zuweisen",
        icon: React.createElement(PersonAdd20Regular),
        initialSize: { width: 560, height: 700 },
        center: true,
        render: () => React.createElement(MailContactAssignWindow, { windowId, ...params }),
    });
    return windowId;
};

export default MailContactAssignWindow;
